// This comment tells ESLint that __webpack_public_path__ is a global variable so it doesn't complain about it
/* global __webpack_public_path__ */
import type { Job, User } from "@eai-toolkit/client";
import { q, tagQClause } from "@eai-toolkit/client";
import type { AxiosInstance } from "axios";
import axios from "axios";
import qs from "qs";

import { qlTag } from "../constants";
import type { JobConfig } from "../types/api";
import type {
  CreateDataObjectResponse,
  CreateJobResponse,
  GetIdentityResponse,
  GetJobResponse,
  GetJobsResponse,
  RetryJobResponse,
} from "../types/responses";

const REQUEST_TIMEOUT = 30000;

export function createAxiosInstance(): AxiosInstance {
  const axiosInstance = axios.create({
    baseURL: `${__webpack_public_path__}webapp/v1`,
    timeout: REQUEST_TIMEOUT,
    withCredentials: true,
    headers: {
      Accept: "application/json",
    },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "repeat" }),
  });

  return axiosInstance;
}

export class ApiClient {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  async getAllResources<T>(opts: {
    pathname: string;
    queryParams?: Record<
      string,
      string | number | boolean | Array<string | number | boolean>
    >;
  }): Promise<T[]> {
    const limit = 500;
    let offset = 0;
    let total: number;
    let results: T[] = [];
    do {
      const res: {
        data: {
          metadata: { total: number };
          items: T[];
        };
      } = await this.axiosInstance({
        method: "get",
        url: opts.pathname,
        params: {
          ...opts.queryParams,
          offset,
          limit,
        },
      });
      total = res.data.metadata.total;
      results = results.concat(res.data.items);
    } while ((offset = offset + limit) < total);
    return results;
  }

  async getMe(): Promise<GetIdentityResponse> {
    const url = "/me";
    const result = await this.axiosInstance.get(url);

    return result.data;
  }

  async getJobById(jobId: string): Promise<GetJobResponse> {
    const url = `/job/${jobId}`;
    const result = await this.axiosInstance.get(url);
    return result.data;
  }

  async getJobs(user?: User): Promise<GetJobsResponse> {
    if (!user) {
      throw new Error("Missing user.");
    }
    return this.getAllResources({
      pathname: "/cluster/job",
      queryParams: {
        q: q(tagQClause({ key: qlTag }), {
          fieldName: "creator",
          operator: "eq",
          value: user.id,
        }),
        order: "-created",
        human: true,
      },
    });
  }

  async retryJob(jobId: string): Promise<RetryJobResponse> {
    const url = `/job/${jobId}/retry`;
    const result = await this.axiosInstance.put(url);
    return result.data;
  }

  async createDataObject(
    accountId: string,
    dataName: string,
  ): Promise<CreateDataObjectResponse> {
    const url = `/account/${accountId}/data`;
    const body = {
      name: dataName,
      tags: [{ qlTag: qlTag }],
    };
    const result = await this.axiosInstance.post(url, body);
    return result.data;
  }

  async submitNotebookJob(
    accountId: string,
    jobParams: JobConfig,
  ): Promise<CreateJobResponse> {
    const baseUrl = new URL(__webpack_public_path__, window.location.origin);
    const { host } = baseUrl;
    const url = `/account/${accountId}/job`;

    const {
      sourceData,
      artifactDataId,
      jobName,
      imageId,
      useLab,
      cpu,
      mem,
      gpu,
      gpuMem,
      dataAccessLevels,
    } = jobParams;

    const appClassName = useLab ? "LabApp" : "NotebookApp";

    const writeAccessMap = new Map<string, boolean>();

    if (dataAccessLevels && dataAccessLevels.length > 0) {
      dataAccessLevels.forEach((level) => {
        writeAccessMap.set(level.urn, level.hasWriteAccess);
      });
    }

    const dataWithAccessModes = sourceData.map((data) => {
      const accessMode = writeAccessMap.has(data.urn)
        ? writeAccessMap.get(data.urn)
          ? ":rw"
          : ":ro"
        : "";

      return {
        data,
        accessMode,
      };
    });

    const jobData: Partial<Job> = {
      data: [
        `${artifactDataId}:/home/toolkit/work:rw`,
        // TODO: name the dataset folder based on the attributes of the data
        ...dataWithAccessModes.map(
          ({ data, accessMode }) =>
            `${data.fullName || data.id}:/home/toolkit/${
              data.fullName || "dataset"
            }${accessMode}`,
        ),
        "shared.notebooks_support.readme:/home/toolkit/README_TOOLKIT:ro",
      ],
      environmentVars: Object.entries({
        ...(useLab ? { JUPYTER_ENABLE_LAB: "true" } : {}),
        PIP_TARGET: "/home/toolkit/work/python_packages",
        PYTHONPATH: "/home/toolkit/work/python_packages",
      }).map(([key, value]) => `${key}=${value}`),
      command: [
        "/bin/sh",
        "-cx",
        "mkdir -p /home/toolkit/work/python_packages; " +
          `start-notebook.sh --ip=0.0.0.0 --port=8080 --no-browser ` +
          `--${appClassName}.token='' ` +
          `--${appClassName}.custom_display_url=https://\${EAI_JOB_ID}.job.` +
          host +
          ` ` +
          `--${appClassName}.allow_remote_access=True ` +
          `--${appClassName}.allow_origin='*' ` +
          `--${appClassName}.disable_check_xsrf=True`,
      ],
      image: imageId,
      isProcessAgent: false,
      name: jobName,
      tags: [{ key: qlTag }],
      workdir: "/home/toolkit",
      preemptable: true,
      resources: {
        cpu,
        mem,
        gpu,
        gpuMem: gpuMem ?? undefined,
      },
    };
    const result = await this.axiosInstance.post(url, jobData);
    return result.data;
  }
}
